import {
  ContentType,
  Filter,
  getStoreNewsCardSizeInProducts,
  getVerticalBySubVertical,
  hasMultipleSubVerticals,
  includesSubVertical,
  RelationProductType,
  Sort,
  SubVertical,
  Vertical,
} from 'common/constants'
import { convertStringBooleanToBoolean } from '@shopping/common'
import { EventParam } from 'common/types/entities/event'
import { DisplayType } from 'common/ducks/menu'
import { StoreCategory } from 'common/types/entities/mongo/storeCategory'
import { AgrnBssCd } from 'common/constants/rank'
import { ReviewContentClassType } from 'common/types/entities/api/review'

export interface ListCommonParam {
  page: number
  pageSize: number
  sort?: Sort
  filter?: Filter
  maxPage?: number
  displayType?: DisplayType
}

export interface ProductParam extends ListCommonParam {
  subVertical?: SubVertical
  includeZzim?: boolean
  includeViewCount?: boolean
  includeItemCount?: boolean
  includeStoreCardInfo?: boolean
  includeStockQuantity?: boolean
  includeBrandInfo?: boolean
  includeRepresentativeReview?: boolean
  npay?: boolean
  filterSoldOut?: boolean
  menuId?: string
  menuIds?: string[]
  storeId?: string
  storeIds?: string[]
  brandId?: string
  brandIds?: string[]
  categoryId?: string
  productIds?: string[]
  storeCategoryId?: string
  naverShoppingCategory?: string
  attributeValueId?: string[]
  attributeValueIds?: string[]
  tag?: string
  ids?: string[]
  first?: string
  eventId?: string
  exposure?: boolean
  best?: boolean
  matNvMid?: number
  certifications?: string[]
  averageReviewScore?: number
  totalReviewCount?: number
  includeStoreInfoWithHighRatingReview?: boolean
}

export const DEFAULT_PAGE_SIZE = 10

export const genProductParam = (subVertical: SubVertical, customParam: ProductParam | any) => ({
  subVertical,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  sort: Sort.RECENT,
  filter: null,
  displayType: DisplayType.CATEGORY_HOME,
  includeZzim: true,
  includeViewCount: isEnabledViewCount(subVertical),
  includeItemCount: null,
  includeStoreCardInfo: isEnabledStoreCardInfo(subVertical),
  includeStockQuantity: isEnabledProductStockQuantity(subVertical),
  includeBrandInfo: isEnabledBrandInfo(subVertical),
  includeRepresentativeReview: false,
  npay: null,
  filterSoldOut: null,
  menuId: null,
  storeId: null,
  storeIds: null,
  brandId: null,
  brandIds: null,
  categoryId: null,
  productIds: null,
  storeCategoryId: null,
  naverShoppingCategory: null,
  attributeValueIds: [],
  tag: null,
  first: null,
  certifications: [],
  averageReviewScore: null,
  totalReviewCount: null,
  ...customParam,
})

export interface RelationProductParam extends ListCommonParam {
  subVertical?: SubVertical
  includeZzim?: boolean
  includeViewCount?: boolean
  includeStoreCardInfo?: boolean
  menuId?: string
  storeId?: string
  storeIds?: string[]
  brandId?: string
  brandIds?: string[]
  storeCategoryId?: string
  tag?: string
  relationProductType?: RelationProductType
  productId?: string
  excludeIds?: string[]
  relationFirst?: string
}

export interface CoordiProductParam extends ListCommonParam {
  subVertical?: SubVertical
  includeZzim?: boolean
  includeViewCount?: boolean
  includeStoreCardInfo?: boolean
  menuId?: string
  storeIds?: string[]
  tag?: string
  relationFirst?: string
}

export const DEFAULT_PAGE_REL_PRODUCT_SIZE = 2
export const DEFAULT_PAGE_COORDI_PRODUCT_SIZE = 10

export const genRelationProductParam = (subVertical: SubVertical, customParam?: RelationProductParam | any) => ({
  subVertical,
  page: 1,
  pageSize: DEFAULT_PAGE_REL_PRODUCT_SIZE,
  sort: Sort.RECENT,
  filter: null,
  includeZzim: true,
  includeViewCount: isEnabledViewCount(subVertical),
  includeStoreCardInfo: isEnabledStoreCardInfo(subVertical),
  menuId: null,
  storeId: null,
  storeIds: null,
  brandId: null,
  brandIds: null,
  tag: null,
  relationProductType: getRelationProductType(subVertical),
  excludeIds: null,
  ...customParam,
})

export const genCoordiProductParam = (subVertical: SubVertical, customParam?: CoordiProductParam | any) => ({
  subVertical,
  page: 1,
  pageSize: DEFAULT_PAGE_COORDI_PRODUCT_SIZE,
  sort: Sort.RECENT,
  includeZzim: true,
  includeViewCount: true,
  includeStoreCardInfo: true,
  menuId: null,
  storeIds: null,
  tag: null,
  relationProductType: RelationProductType.COORDI,
  ...customParam,
})

export const homeNewsPageSize = {
  HOME_MEAL: 10,
  DIRECT_FARM: 10,
  LOCAL_FOOD: 10,
}

export interface NewsParam extends ListCommonParam {
  contentType?: ContentType
  subVertical?: SubVertical
  menuId?: string
  storeId?: string
  branchId?: string
  showKeyContent?: boolean
  showPageNumber?: boolean
  showRelatedProducts?: boolean
  includedTypes?: ContentType[]
  storeCategories?: StoreCategory[]
  showBranchName?: boolean
  hasImage?: boolean
}
export const genNewsParam = (subVertical: SubVertical, customParam?: NewsParam | any) => ({
  subVertical,
  page: 1,
  pageSize: getStoreNewsCardSizeInProducts(subVertical),
  sort: Sort.RECENT,
  filter: null,
  contentType: null,
  menuId: null,
  storeId: null,
  branchId: null,
  showKeyContent: null,
  showPageNumber: null,
  showRelatedProducts: null,
  includedTypes: null,
  isBranchNews: null,
  ...customParam,
})

export interface ReviewParam extends ListCommonParam {
  storeId?: number
  sellerNo?: string
}
export const genReviewParam = (customParam?: ReviewParam | any) => ({
  page: 1,
  pageSize: 1,
  ...customParam,
})

export interface RecommendReviewParam extends ListCommonParam {
  subVertical?: SubVertical
  menu?: string
  reviewContentClassTypes?: [ReviewContentClassType]
  reviewContentLength?: number
  includeItemCount?: boolean
}

export const genRecommendReviewParam = (customParam?: RecommendReviewParam | any) => ({
  page: 1,
  pageSize: 1,
  ...customParam,
})

export interface StoreRankingParam extends ListCommonParam {
  vertical?: Vertical
  subVertical?: SubVertical
  storeCategoryId?: string
  agrnBssCd?: AgrnBssCd
}
export const genStoreRankingParam = (subVertical: SubVertical, customParam?: StoreRankingParam | any) => ({
  subVertical,
  page: 1,
  vertical: getVerticalBySubVertical(subVertical),
  storeCategoryId: null,
  parentStoreCategoryId: null,
  agrnBssCd: null,
  ...customParam,
})

export interface SpecialEventParam extends EventParam, ListCommonParam {}
export const genSpecialEventParam = (subVertical: SubVertical, customParam?: SpecialEventParam | any) => ({
  vertical: getVerticalBySubVertical(subVertical),
  subVertical: hasMultipleSubVerticals(getVerticalBySubVertical(subVertical)) ? subVertical : null,
  page: 1,
  pageSize: 1,
  ...customParam,
})

// work-around for using code assist
export type ListSearchField =
  | any
  | ProductParam & NewsParam & ReviewParam & RelationProductParam & SpecialEventParam & StoreRankingParam

export const convertProductParam = (param: ProductParam) => {
  param.page = Number(param.page)
  param.pageSize = Number(param.pageSize)
  param.includeZzim = convertStringBooleanToBoolean(param.includeZzim)
  param.includeViewCount = convertStringBooleanToBoolean(param.includeViewCount)
  param.includeItemCount = convertStringBooleanToBoolean(param.includeItemCount)
  param.includeStoreCardInfo = convertStringBooleanToBoolean(param.includeStoreCardInfo)
  param.includeStockQuantity = convertStringBooleanToBoolean(param.includeStockQuantity)
  param.includeBrandInfo = convertStringBooleanToBoolean(param.includeBrandInfo)
  param.filterSoldOut = convertStringBooleanToBoolean(param.filterSoldOut)
  param.includeRepresentativeReview = convertStringBooleanToBoolean(param.includeRepresentativeReview)
  param.npay = convertStringBooleanToBoolean(param.npay)
  param.exposure = convertStringBooleanToBoolean(param.exposure)
  param.best = convertStringBooleanToBoolean(param.best)
  return param
}

export const convertRelationProductParam = (param: RelationProductParam | CoordiProductParam) => {
  param.page = Number(param.page)
  param.pageSize = Number(param.pageSize)
  param.includeZzim = convertStringBooleanToBoolean(param.includeZzim)
  param.includeViewCount = convertStringBooleanToBoolean(param.includeViewCount)
  param.includeStoreCardInfo = convertStringBooleanToBoolean(param.includeStoreCardInfo)
  return param
}

export const convertNewsParam = (param: NewsParam) => {
  param.page = Number(param.page)
  param.pageSize = Number(param.pageSize)
  param.showKeyContent = convertStringBooleanToBoolean(param.showKeyContent)
  param.showPageNumber = convertStringBooleanToBoolean(param.showPageNumber)
  param.showRelatedProducts = convertStringBooleanToBoolean(param.showRelatedProducts)
  param.showBranchName = convertStringBooleanToBoolean(param.showBranchName)
  return param
}

export const isEnabledStoreCardInfo = includesSubVertical(
  SubVertical.DEPARTMENT,
  SubVertical.OUTLET,
  SubVertical.STYLE,
  SubVertical.HOME_LIVING,
  SubVertical.HANDMADE,
  SubVertical.STATIONERY,
  SubVertical.FOREIGN,
  SubVertical.TRADITIONAL_ALCOHOL,
)
export const isEnabledProductStockQuantity = includesSubVertical(SubVertical.OUTLET)
export const isEnabledViewCount = includesSubVertical(
  SubVertical.DEPARTMENT,
  SubVertical.OUTLET,
  SubVertical.STYLE,
  SubVertical.BEAUTY,
  SubVertical.HOME_LIVING,
  SubVertical.HANDMADE,
  SubVertical.STATIONERY,
  SubVertical.KIDS,
  SubVertical.PLAY,
  SubVertical.ART,
  SubVertical.FOREIGN,
  SubVertical.TRADITIONAL_ALCOHOL,
)
export const isEnabledBrandInfo = includesSubVertical(SubVertical.ART)

export const getRelationProductType = (subVertical: SubVertical) => {
  if (subVertical === SubVertical.DEPARTMENT) return RelationProductType.VMD
  if (subVertical === SubVertical.STYLE) return RelationProductType.COORDI
  return null
}

export const convertRelationProductCountParam = (param: RelationProductParam) => {
  param.page = 1
  param.pageSize = 1
  return param
}
